/**
 * What the CSR asked for — the project name, the estimate's deadline and notes,
 * and its request lines, each with its logo files — the ticket it raised to the
 * merchandise team (and, while it is open, who in the team has claimed it),
 * and the Promo Office quote CIS decided answers it — line by line, with buy
 * unit, Promo Office sell unit and decoration setup charges side by side.
 *
 * The three money columns always appear together: a buy price on its own
 * invites a mental margin against nothing, and a sell price on its own
 * reads as ours.
 *
 * **Every figure here is served, never computed.** The buy and sell totals are
 * the backend's (`costChange.currentBuyTotalPence` while a quote has moved,
 * `pricing.buyTotalPence` otherwise) and each line carries its own total. The
 * card used to recompute them from unit x quantity, which is both a business
 * rule in the frontend and the reason it could disagree with the price
 * build-up two cards away.
 */
import { Link } from "react-router";
import { FileText, Paperclip, Ticket, TriangleAlert } from "lucide-react";
import type {
  LinkedPromoOfficeQuote,
  MerchandiseDraftEstimate,
  MerchandiseRequestLine,
} from "@shared-core/data/merchandise-types";
import {
  formatBytes,
  formatDateTimeOrDash,
  formatLongDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import {
  describeOpenFor,
  describeTicketClaim,
} from "@shared-core/domain/merchandise-presentation";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";

function Field({
  label,
  value,
  gap = "Not given",
}: {
  label: string;
  value: React.ReactNode | null | undefined;
  gap?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
      <dd className="text-sm break-words">
        {value == null || value === "" ? (
          <span className="text-muted-foreground italic">{gap}</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

export function DraftEstimateCard({
  request,
}: {
  request: MerchandiseDraftEstimate;
}) {
  const { request: details, merchandiseTeamTicket: ticket } = request;
  const claim = describeTicketClaim(ticket);
  return (
    <div className="space-y-6">
      <section aria-label="The request" className="space-y-3">
        <h4 className="text-sm font-semibold">The request</h4>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Project name" value={details.projectName} />
          </div>
          {/* One deadline for the whole estimate (decided 2026-10-07). Null
              only on the drafts raised before it was asked for. */}
          <Field
            label="Needed by"
            value={
              details.deadline ? formatLongDateOrDash(details.deadline) : null
            }
          />
          <div className="sm:col-span-2 lg:col-span-3">
            <Field
              label="Notes"
              value={
                details.notes ? (
                  <span className="whitespace-pre-line">{details.notes}</span>
                ) : null
              }
              gap="None"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Field
              label="Delivery notes"
              value={details.deliveryNotes}
              gap="None"
            />
          </div>
        </dl>
        <section aria-label="Request lines" className="space-y-2">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Request lines
          </h5>
          {details.lines.length > 0 ? (
            <ol className="space-y-3">
              {details.lines.map((line) => (
                <RequestLine key={line.id} line={line} />
              ))}
            </ol>
          ) : (
            // Only the drafts raised before request lines existed.
            <p className="text-sm text-muted-foreground italic">
              No request lines were recorded on this draft.
            </p>
          )}
        </section>
        <p className="text-xs text-muted-foreground">
          Raised {formatDateTimeOrDash(request.createdAt)} by{" "}
          {request.createdBy.name} for {request.customer.name} (
          <span className="font-mono">{request.customer.code}</span>).
        </p>
      </section>

      <section
        aria-label="Ticket to the merchandise team"
        className="space-y-2"
      >
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <Ticket className="size-4 text-muted-foreground" aria-hidden />
          Ticket to the merchandise team
        </h4>
        {ticket ? (
          <p className="text-sm">
            <span className="font-mono">{ticket.id}</span> raised{" "}
            {formatDateTimeOrDash(ticket.raisedAt)}
            {ticket.resolvedAt ? (
              <> · resolved {formatDateTimeOrDash(ticket.resolvedAt)}</>
            ) : ticket.raisedAt ? (
              <>
                {" · "}
                <Badge variant="neutral">
                  open for {describeOpenFor(ticket.raisedAt)}
                </Badge>
              </>
            ) : null}
            {/* Who in the team is working on it, while the ticket is open. */}
            {claim && <> · {claim}</>}
          </p>
        ) : (
          // A real state, not a bug: ticket creation is best effort, and the
          // request still stands. Saying so beats an empty heading.
          <p className="text-sm text-muted-foreground">
            No ticket was raised for this request — tell the merchandise team
            directly, quoting the reference below.
          </p>
        )}
        <p className="text-xs text-muted-foreground max-w-prose">
          The ticket carries this request, its lines and the reference{" "}
          <span className="font-mono select-all">{request.reference}</span>. The
          merchandise team quotes in Promo Office and types the reference into
          the quote&apos;s Customer Ref; the quote attaches itself here on the
          next refresh.
        </p>
      </section>

      <section aria-label="Promo Office quote" className="space-y-3">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <FileText className="size-4 text-muted-foreground" aria-hidden />
          Promo Office quote
        </h4>
        {request.linkedQuote ? (
          <>
            {request.linkedQuote.projectNameMismatch && (
              <ProjectNameMismatchNotice
                quoteProjectName={request.linkedQuote.quoteProjectName}
                projectName={details.projectName}
              />
            )}
            <LinkedQuoteLines
              quote={request.linkedQuote}
              buyTotalPence={
                request.costChange?.currentBuyTotalPence ??
                request.pricing?.buyTotalPence ??
                null
              }
            />
            {/* The quote's lines are above; the price they add up to belongs
                beside them, not two cards away. */}
            {request.pricing && (
              <p className="text-sm">
                Quoted price for this request:{" "}
                <span className="font-medium tabular-nums">
                  {formatQuotedPrice(request.pricing.ourPricePence)}
                </span>{" "}
                <span className="text-muted-foreground">
                  (the merchandise team&apos;s price on a buy price of{" "}
                  {formatQuotedPrice(request.pricing.buyTotalPence)}, a{" "}
                  {request.pricing.marginPct}% margin — see Financial Details)
                </span>
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground max-w-prose">
            No Promo Office quote is linked yet. If the merchandise team saves a
            quote without the reference it appears in the{" "}
            <Link to={paths.promoOffice()} className="underline">
              Unlinked quotes lane on the Promo Office page
            </Link>
            , where it can be attached to this request by hand.
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * The backend decided at link time that the quote's Project Name is not this
 * request's. A prompt to check, not a refusal: the quote is linked and priced.
 */
function ProjectNameMismatchNotice({
  quoteProjectName,
  projectName,
}: {
  quoteProjectName: string | null | undefined;
  projectName: string;
}) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-md border border-[color:var(--color-warning)] p-3"
    >
      <TriangleAlert
        className="size-4 shrink-0 mt-0.5 text-[color:var(--color-warning)]"
        aria-hidden
      />
      <div className="text-sm space-y-1 min-w-0">
        <p className="font-medium">Check this is the right quote</p>
        <p className="text-muted-foreground break-words">
          The quote&apos;s project name is{" "}
          {quoteProjectName ? (
            <span className="text-foreground">“{quoteProjectName}”</span>
          ) : (
            "blank"
          )}
          , but this request is{" "}
          <span className="text-foreground">“{projectName}”</span>. If the quote
          is for a different request, unlink it.
        </p>
      </div>
    </div>
  );
}

function RequestLine({ line }: { line: MerchandiseRequestLine }) {
  return (
    <li
      aria-label={`Line ${line.lineNo}: ${line.itemType}`}
      className="rounded-md border p-3 space-y-3"
    >
      <p className="text-sm font-medium break-words">
        <span className="text-muted-foreground">{line.lineNo}.</span>{" "}
        {line.itemType}
      </p>
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Quantity" value={line.quantity} />
        <Field label="Logo colours" value={line.logoColours} />
        <Field label="Budget" value={line.budget} />
        <Field label="Sourcing City code" value={line.sourcingCityCode} />
        <Field
          label="Logo"
          value={
            line.attachments.length > 0 ? (
              <ul className="space-y-0.5">
                {line.attachments.map((file) => (
                  <li key={file.id} className="flex items-center gap-1.5">
                    <Paperclip
                      className="size-3.5 text-muted-foreground"
                      aria-hidden
                    />
                    {/* The owning module's auth-gated download route, taken
                        from the response — the storage path is never exposed
                        and the URL is never built here. */}
                    <a href={file.downloadUrl} className="underline">
                      {file.name}
                    </a>
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(file.sizeBytes)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null
          }
          gap="No logo attached"
        />
      </dl>
    </li>
  );
}

/**
 * ``buyTotalPence`` is the figure the backend stands behind *now* — the
 * changed cost while a quote has moved, the priced-at cost otherwise. Null
 * only in the state this component is not rendered in (no quote, no price).
 * The Promo Office sell total is a plain sum of the served line totals: a
 * presentation aggregate over served columns, not a pricing formula.
 */
function LinkedQuoteLines({
  quote,
  buyTotalPence,
}: {
  quote: LinkedPromoOfficeQuote;
  buyTotalPence: number | null;
}) {
  const buyTotal = buyTotalPence;
  const sellTotal = quote.lines.reduce(
    (sum, line) =>
      sum +
      line.promoOfficeSellLineTotalPence +
      line.decorations.reduce(
        (acc, d) => acc + d.setupChargePromoOfficeSellPence,
        0,
      ),
    0,
  );
  return (
    <div className="space-y-2">
      <p className="text-sm">
        Quote <span className="font-mono">{quote.promoOfficeQuoteNumber}</span>,
        linked {formatDateTimeOrDash(quote.linkedAt)}{" "}
        {quote.linkedBy === "reference"
          ? "by its reference"
          : "by hand from the Unlinked quotes lane"}
        . Last updated in Promo Office{" "}
        {formatDateTimeOrDash(quote.promoOfficeUpdatedAt)}.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-3 py-2">Article</th>
              <th className="text-left font-medium px-3 py-2">Description</th>
              <th className="text-right font-medium px-3 py-2">Quantity</th>
              <th className="text-right font-medium px-3 py-2">Buy unit</th>
              <th className="text-right font-medium px-3 py-2">
                Promo Office sell unit
              </th>
              <th className="text-right font-medium px-3 py-2">Buy total</th>
            </tr>
          </thead>
          <tbody>
            {quote.lines.map((line) => (
              <LineRows key={line.id} line={line} />
            ))}
          </tbody>
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className="px-3 py-2" colSpan={3}>
                Totals
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {buyTotal === null ? "—" : formatQuotedPrice(buyTotal)}
                <span className="block text-xs font-normal text-muted-foreground">
                  Buy price
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatQuotedPrice(sellTotal)}
                <span className="block text-xs font-normal text-muted-foreground">
                  Promo Office sell
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {buyTotal === null ? "—" : formatQuotedPrice(buyTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function LineRows({ line }: { line: LinkedPromoOfficeQuote["lines"][number] }) {
  return (
    <>
      <tr className="border-t border-border">
        <td className="px-3 py-2 font-mono text-xs">
          {line.articleNumber ?? "—"}
        </td>
        <td className="px-3 py-2">{line.description}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {line.quantity.toLocaleString("en-GB")}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatQuotedPrice(line.buyUnitPence)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
          {formatQuotedPrice(line.promoOfficeSellUnitPence)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatQuotedPrice(line.buyLineTotalPence)}
        </td>
      </tr>
      {line.decorations.map((decoration) => (
        <tr key={decoration.id} className="text-muted-foreground">
          <td className="px-3 py-1.5 pl-6 text-xs">Decoration</td>
          <td className="px-3 py-1.5">
            {decoration.description}
            <span className="block text-xs">
              Setup charge {formatQuotedPrice(decoration.setupChargeBuyPence)}{" "}
              buy
              {" · "}
              {formatQuotedPrice(
                decoration.setupChargePromoOfficeSellPence,
              )}{" "}
              Promo Office sell
            </span>
          </td>
          <td className="px-3 py-1.5 text-right tabular-nums">
            {decoration.quantity.toLocaleString("en-GB")}
          </td>
          {/* Promo Office states a decoration as one flat total with no
              per-unit split, so these two are 0 on every real quote. The
              fields stay (a future vendor split needs no migration) but an
              always-zero column reads as "free", which it is not. */}
          <td className="px-3 py-1.5 text-right tabular-nums">
            {decoration.buyUnitPence
              ? formatQuotedPrice(decoration.buyUnitPence)
              : "—"}
          </td>
          <td className="px-3 py-1.5 text-right tabular-nums">
            {decoration.promoOfficeSellUnitPence
              ? formatQuotedPrice(decoration.promoOfficeSellUnitPence)
              : "—"}
          </td>
          <td className="px-3 py-1.5 text-right tabular-nums">
            {formatQuotedPrice(
              decoration.buyUnitPence * decoration.quantity +
                decoration.setupChargeBuyPence,
            )}
          </td>
        </tr>
      ))}
    </>
  );
}
